import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import icons from '@web-packages/icons';
import Box from '@mui/material/Box';

import React, { useState, useRef, forwardRef } from 'react';
import { TextInputProps } from './types';

const handleHideShowPassword = (showPassword: boolean) => (showPassword ? 'text' : 'password');

const TextInput = forwardRef<HTMLInputElement, TextInputProps>((
  {
    id,
    label,
    helperText,
    errorText,
    warningText,
    placeholder = '',
    required = false,
    disabled = false,
    readOnly = false,
    isSmall = undefined,
    multiline = 0,
    onChange,
    icon = undefined,
    // inputRef,
    type = 'text',
    maxLength = 100,
    min = undefined,
    max = undefined,
    value = '',
    onClick = undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onKeyDown = null,
    onKeyUpAction = undefined,
    onBlur = undefined,

    showAllText = false,
    disableClearable = true,
    fullWidth = false,
  },
  ref,
) => {
  //   startAdornment,
  //   endAdornment,
  // }: TextInputProps) => {
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = ref || localInputRef;

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const isPassword = type === 'password';

  const size = isSmall ? 'small' : undefined;

  const selectInput = () => {
    if (localInputRef?.current && localInputRef.current !== null) {
      localInputRef.current.select();
    }
    if (onClick) {
      onClick();
    }
  };
  const shrinkStyle = showAllText ? { shrink: true } : {};

  const StartAdornment = icon ? icons?.[icon] : null;
  const EndIconButton = !disableClearable && !isPassword ? icons.close : null;

  return (
    <Box
      id={`text-field-div-${id}`}
      sx={{
        width: '100%',
        // position: 'relative',
        ':hover .end-icon, :focus-within .end-icon': {
          visibility: value || isPassword ? 'visible' : 'hidden',
        },
      }}
    >
      <TextField
        title=''
        value={value}
        fullWidth={fullWidth}
        sx={{
          width: '100%',
          input: {
            cursor: onClick && !disabled ? 'pointer' : 'auto',
            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
              display: 'none',
              margin: 0,
            },
            MozAppearance: 'textfield',
          },
          '.MuiFormHelperText-root': {
            color: warningText && !errorText ? 'custom.warning' : null,
          },
        }}
        inputRef={inputRef}
        multiline={multiline !== 0}
        rows={multiline !== 0 ? multiline : 1}
        onClick={onClick}
        onKeyDown={(event: React.KeyboardEvent) => { onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>, inputRef); }}
        onKeyUp={(event: React.KeyboardEvent) => {
          if (onKeyUpAction?.keys?.includes(event.key)) {
            onKeyUpAction?.action?.();
          }
        }}
        onBlur={onBlur}
        error={!!errorText}
        helperText={errorText || warningText || helperText}
        placeholder={placeholder}
        autoComplete="off"
        type={isPassword ? handleHideShowPassword(showPassword) : type}
        label={label}
        variant="outlined"
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        size={size}
        slotProps={{
          input: {
            readOnly,
            inputProps: {
              maxLength,
              min,
              max,
            },
            startAdornment: StartAdornment && (
              <InputAdornment position="start">
                <StartAdornment
                  sx={{
                    marginRight: '2px',
                    width: '1em',
                    height: '1em',
                    display: 'inline-block',
                    fill: 'currentcolor',
                    flexShrink: 0,
                    transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                    fontSize: '1.25rem',
                  }}
                  onClick={selectInput}
                />
              </InputAdornment>
            ),
            endAdornment: ((disableClearable === false || isPassword) && (
              <InputAdornment position="end">
                <IconButton
                  className="end-icon"
                  sx={{
                    height: '28px',
                    width: '28px',
                    padding: '4px',
                    margin: '0px -2px 0px 0px',
                    '&.Mui-focused .MuiIconButton-root': {
                      color: 'primary.main',
                    },
                    visibility: isPassword ? 'visible' : 'hidden',
                  }}
                  onClick={() => {
                    if (!disableClearable && !isPassword) {
                      onChange('');
                      selectInput();
                    } else {
                      handleClickShowPassword();
                    }
                  }}
                >
                  {EndIconButton && (
                    <EndIconButton
                      sx={{
                        width: '1em',
                        height: '1em',
                        display: 'inline-block',
                        fill: 'currentcolor',
                        flexShrink: 0,
                        transition: 'fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                        fontSize: '1.25rem',
                      }}
                    />
                  )}
                  {isPassword && (showPassword ? <Visibility /> : <VisibilityOff />)}
                </IconButton>
              </InputAdornment>
            )),
          },
          inputLabel: shrinkStyle
        }}
      />
    </Box>
  );
});

TextInput.displayName = 'TextInput';

export default TextInput;
